import {
  Component,
  ComponentRef,
  Injector,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzRadioGroupComponent, NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { NzUploadFile, NzUploadModule } from 'ng-zorro-antd/upload';
import { QuestionnairesService } from './questionnaires.service';
import { ManageSettingsService } from '../../../../manage-settings/manage-settings.service';
import {
  firstValueFrom,
  forkJoin,
  from,
  Observable,
  of,
  Subject,
  takeUntil,
} from 'rxjs';
import {
  allowedFileExtensions,
  ExtendedUploadFile,
  ManageQuestionnaireDrawerParams,
  QuestionAnswers,
  QuestionFile,
  QuestionnaireMode,
  QuestionnairesAnswerResponse,
  QuestionnairesPayload,
  QuestionnairesResponse,
  FileData,
} from './questionnaires.interface';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { DrawerService } from '../../../../../core/services/drawer.service';
import {
  LinearScale,
  QuestionnaireResponse,
} from '../../../../manage-settings/manage-settings.interface';
import { CommonService } from '../../../../../shared/services/common.service';
import { ErrorResponse } from '../../../../../core/models/error.models';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AnswerType } from '../../../../manage-settings/manage-settings.enums';
import { CommonModule } from '@angular/common';
import { LoaderService } from '../../../../../core/services/loader.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { format } from 'date-fns';
import { OrganizationService } from '../../../../organizations/organization.service';
import { PrintQuestionnairesFormComponent } from './print-questionnaires-form/print-questionnaires-form.component';
import { StudentAttachmentService } from '../student-attachment.service';
import { OrganizationModel } from '../../../../organizations/organization.interface';
import { ApiResponse } from '../../../../../core/models/api-response.models';
import { inRange, intersection, map } from 'lodash';
import { AuditContextService } from '../../../../audit-logs/audit-context.service';

@Component({
  selector: 'app-questionnaires-form',
  standalone: true,
  imports: [
    NzFormModule,
    NzInputModule,
    NzDividerModule,
    NzRadioModule,
    NzCheckboxModule,
    NzRadioGroupComponent,
    NzUploadModule,
    NzButtonModule,
    SvgIconComponent,
    NzDatePickerModule,
    NzTimePickerModule,
    NzSelectModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzSpinModule,
  ],
  templateUrl: './questionnaires-form.component.html',
  styleUrl: './questionnaires-form.component.scss',
})
export class QuestionnairesFormComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  caseParams?: ManageQuestionnaireDrawerParams =
    {} as ManageQuestionnaireDrawerParams;
  questionList: QuestionnaireResponse[] = [];
  questionAnswersList: QuestionnairesAnswerResponse[] = [];
  form!: FormGroup;
  answerType = AnswerType;
  loading$: Observable<boolean> = of(false);
  timeControls: string[] = [];
  fileControls: string[] = [];
  questionAnswerResponses = {} as QuestionnairesResponse;
  attachmentName?: string = '';
  organizationDetail = {} as OrganizationModel;
  uploadedFiles: QuestionFile[] = [];
  allowedFileExtensions = allowedFileExtensions;
  titlePattern = /^\S.*$/;
  constructor(
    private readonly manageSettingsService: ManageSettingsService,
    private readonly questionnairesService: QuestionnairesService,
    private readonly drawerService: DrawerService,
    private readonly commonService: CommonService,
    private readonly loaderService: LoaderService,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly injector: Injector,
    private readonly orgService: OrganizationService,
    private readonly studentAttachmentService: StudentAttachmentService,
    private readonly auditContextService: AuditContextService
  ) {
    this.loading$ = this.loaderService.loading$;
  }

  ngOnInit(): void {
    this.subscribeToDrawer();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToDrawer(): void {
    this.drawerService
      .getParams<ManageQuestionnaireDrawerParams>(
        DRAWER_IDS.CASE_ATTACHMENT_QUESTIONNAIRE
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(async params => {
        if (!params) return;
        this.caseParams = params;
        await this.loadQuestions();
        this.getOrganizationDetail();
      });
  }

  async getOrganizationDetail() {
    if (this.caseParams) {
      this.organizationDetail = await firstValueFrom(
        this.orgService
          .getOrganization(this.caseParams?.case?.student.organizationId || 0)
          .pipe(takeUntil(this.destroy$))
      );
    }
  }

  get trimAttachmentName(): string {
    return this.attachmentName?.trimStart().trimEnd() ?? '';
  }

  private async loadQuestions(): Promise<void> {
    if (!this.caseParams) return;
    const response = await this.safeApiCall(() =>
      this.manageSettingsService.getQuestionsByPlanType(
        this.caseParams?.planTypeId || 0
      )
    );
    if (!response) return;

    this.questionList = response.result;
    this.createForm();

    if (this.caseParams?.attachment) {
      await this.loadAnswers(this.caseParams.attachment.id);
    } else {
      this.loaderService.hide();
    }
  }

  private createForm(): void {
    const controls: Record<string, FormControl> = {};

    this.fileControls = [];
    this.timeControls = [];

    for (const q of this.questionList) {
      const controlName = `que_${q.id}`;
      switch (q.answerTypeId) {
        case this.answerType.CHECKBOX:
          controls[controlName] = new FormControl([], [Validators.required]);
          break;
        case this.answerType.FILEUPLOAD:
          this.fileControls.push(controlName);
          controls[controlName] = new FormControl([], [Validators.required]);
          break;
        case this.answerType.TIME:
          this.timeControls.push(controlName);
          controls[controlName] = new FormControl(null, [Validators.required]);
          break;
        case this.answerType.TEXTBOX:
          controls[controlName] = new FormControl(null, [
            Validators.required,
            Validators.maxLength(255),
            Validators.pattern(this.titlePattern),
          ]);
          break;
        case this.answerType.PARAGRAPH:
          controls[controlName] = new FormControl(null, [
            Validators.required,
            Validators.maxLength(1000),
            Validators.pattern(this.titlePattern),
          ]);
          break;
        default:
          controls[controlName] = new FormControl('', [Validators.required]);
      }
    }

    this.form = new FormGroup(controls);
  }

  private async loadAnswers(attachmentId: number): Promise<void> {
    const response = await this.safeApiCall(() =>
      this.questionnairesService.getQuestionsResponseByAttachmentId(
        attachmentId
      )
    );
    if (!response) return;

    this.questionAnswersList = response.result;
    this.setFormAnswers();
  }

  private async setFormAnswers(): Promise<void> {
    if (this.questionAnswersList.length > 0) {
      this.attachmentName = this.questionAnswersList[0].questionnaireName;
    }

    for (const question of this.questionList) {
      const control = this.form.get(`que_${question.id}`);
      if (!control) continue;

      const answer = this.questionAnswersList.find(
        a => a.questionId === question.id
      );

      const value = answer
        ? await this.extractAnswerValue(answer, question)
        : null;

      control.setValue(value);
    }
    this.loaderService.hide();
  }

  private async extractAnswerValue(
    answer: QuestionnairesAnswerResponse,
    question: QuestionnaireResponse
  ): Promise<unknown> {
    const answerTypeId = Number(answer.answerType);

    switch (answerTypeId) {
      case this.answerType.CHECKBOX:
        return this.extractCheckboxValue(answer, question);

      case this.answerType.RADIOBUTTON:
      case this.answerType.DROPDOWN:
        return this.extractOptionValue(answer, question);

      case this.answerType.LINEARSCALE:
        return this.extractLinearScaleValue(answer, question);

      case this.answerType.TEXTBOX:
      case this.answerType.PARAGRAPH:
        return answer.answerValue;

      case this.answerType.DATE:
        return new Date(answer.answerValue);

      case this.answerType.TIME:
        return this.convertToDate(answer.answerValue);

      case this.answerType.FILEUPLOAD:
        return this.extractFileUploadValue(answer);

      default:
        return answer.answerValue ?? null;
    }
  }

  private extractCheckboxValue(
    answer: QuestionnairesAnswerResponse,
    question: QuestionnaireResponse
  ): number[] {
    const answerValue = this.stringToArray(answer.answerValue);
    return intersection(answerValue, map(question.options, 'id'));
  }

  private extractOptionValue(
    answer: QuestionnairesAnswerResponse,
    question: QuestionnaireResponse
  ): number | string {
    const answerValue = Number(answer.answerValue);
    return question.options.some(opt => opt.id === answerValue)
      ? answerValue
      : '';
  }

  private extractLinearScaleValue(
    answer: QuestionnairesAnswerResponse,
    question: QuestionnaireResponse
  ): number | null {
    const answerValue = Number(answer.answerValue);
    const minValue = Number(question.linearScale?.minScaleValue) || 0;
    const maxValue = Number(question.linearScale?.maxScaleValue);

    return inRange(answerValue, minValue, maxValue) ? answerValue : null;
  }

  private async extractFileUploadValue(
    answer: QuestionnairesAnswerResponse
  ): Promise<ExtendedUploadFile[]> {
    return answer?.files.length > 0
      ? await this.getFilesFromUrl(answer.files)
      : [];
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid || !this.caseParams) {
      Object.keys(this.form.controls).forEach(key => {
        this.getControl(key)?.markAsTouched();
      });
      return;
    }
    const responses = this.buildResponses();
    const payload = this.buildPayload(responses);

    // Build audit trail for modified fields
    const _modifiedFields = this.buildQuestionResponseModifiedFields(responses);
    if (_modifiedFields.length > 0) {
      (payload as unknown as Record<string, unknown>)['_modifiedFields'] =
        _modifiedFields;
    }

    const response = await this.safeApiCall(() =>
      this.questionnairesService.SaveOrUpdateQuestionResponse(payload)
    );
    if (!response) return;

    this.questionAnswerResponses = response.result;
    await this.handleUploadsAndAttachment(response.result);
  }

  private buildResponses(): QuestionAnswers[] {
    return this.questionList.map(q => {
      const value = this.form.get(`que_${q.id}`)?.value;

      switch (q.answerTypeId) {
        case AnswerType.TIME:
          return {
            questionId: q.id,
            answerText: value ? format(value, 'HH:mm') : '',
          };
        case AnswerType.CHECKBOX:
          return {
            questionId: q.id,
            answerText: '',
            selectedOptionIds: value ?? [],
          };
        case AnswerType.FILEUPLOAD:
          return { questionId: q.id, answerText: '' };
        default:
          return { questionId: q.id, answerText: value };
      }
    });
  }

  private buildPayload(responses: QuestionAnswers[]): QuestionnairesPayload {
    return {
      caseId: this.caseParams?.case?.id || 0,
      planTypeId: this.caseParams?.planTypeId || 1,
      caseAttachmentId:
        this.caseParams?.mode === QuestionnaireMode.EDIT
          ? this.caseParams.attachment?.id
          : undefined,
      attachmentName: this.attachmentName,
      responses,
    };
  }

  private buildQuestionResponseModifiedFields(
    currentResponses: QuestionAnswers[]
  ): Record<string, unknown>[] {
    // For CREATE mode - all responses are new
    if (
      !this.caseParams?.attachment ||
      this.caseParams?.mode === QuestionnaireMode.CREATE
    ) {
      return currentResponses.flatMap(response => {
        const question = this.questionList.find(
          q => q.id === response.questionId
        );
        const questionText =
          question?.questionText || `Question ${response.questionId}`;
        const fields: Record<string, unknown>[] = [];

        if (response.answerText) {
          fields.push({
            fieldLabel: `${questionText} - Answer`,
            oldValue: null,
            newValue: response.answerText,
            isSensitive: false,
          });
        }

        if (
          response.selectedOptionIds &&
          response.selectedOptionIds.length > 0
        ) {
          const selectedOptions = response.selectedOptionIds
            .map(optId => {
              const option = question?.options?.find(opt => opt.id === optId);
              return option?.optionValue || `Option ${optId}`;
            })
            .join(', ');

          fields.push({
            fieldLabel: `${questionText} - Selected Options`,
            oldValue: null,
            newValue: selectedOptions,
            isSensitive: false,
          });
        }

        return fields;
      });
    }

    // For EDIT mode - compare with existing responses
    return currentResponses.flatMap(response => {
      const question = this.questionList.find(
        q => q.id === response.questionId
      );
      const questionText =
        question?.questionText || `Question ${response.questionId}`;
      const existingResponse = this.questionAnswersList.find(
        a => a.questionId === response.questionId
      );
      const fields: Record<string, unknown>[] = [];

      // Track answer text changes
      if (response.answerText) {
        const oldValue = existingResponse?.answerValue || null;
        if (oldValue !== response.answerText) {
          fields.push({
            fieldLabel: `${questionText} - Answer`,
            oldValue,
            newValue: response.answerText,
            isSensitive: false,
          });
        }
      }

      // Track selected options changes
      if (response.selectedOptionIds && response.selectedOptionIds.length > 0) {
        const selectedOptions = response.selectedOptionIds
          .map(optId => {
            const option = question?.options?.find(opt => opt.id === optId);
            return option?.optionValue || `Option ${optId}`;
          })
          .join(', ');

        const oldValue = existingResponse?.answerValue || null;
        if (oldValue !== selectedOptions) {
          fields.push({
            fieldLabel: `${questionText} - Selected Options`,
            oldValue,
            newValue: selectedOptions,
            isSensitive: false,
          });
        }
      }

      return fields;
    });
  }

  private buildAttachmentModifiedFields(): Record<string, unknown>[] {
    return [
      ...(this.attachmentName
        ? [
            {
              fieldLabel: 'Attachment Name',
              oldValue: null,
              newValue: this.attachmentName,
              isSensitive: false,
            },
          ]
        : []),
      {
        fieldLabel: 'Description',
        oldValue: null,
        newValue: 'Questionnaire Document',
        isSensitive: false,
      },
      {
        fieldLabel: 'Media Type',
        oldValue: null,
        newValue: 'PDF',
        isSensitive: false,
      },
    ];
  }

  private async handleUploadsAndAttachment(result: QuestionnairesResponse) {
    const fileUploads = this.prepareFileUploads(result);

    if (fileUploads.length > 0) {
      const filesResponses = await this.uploadFiles(fileUploads);
      const allFiles = filesResponses.flatMap(res => res.result);
      this.uploadedFiles = allFiles ?? [];
    }

    await this.prepareQuestionnaireAttachment();
  }

  private prepareFileUploads(result: QuestionnairesResponse) {
    return this.fileControls
      .map(ctrl => {
        const questionId = Number(ctrl.split('_')[1]);
        const files = this.form.get(ctrl)?.value;
        if (files?.length) {
          return {
            questionId,
            caseAttachmentId: result.caseAttachmentId,
            files,
          };
        }
        return null;
      })
      .filter(Boolean) as {
      questionId: number;
      caseAttachmentId: number;
      files: ExtendedUploadFile[];
    }[];
  }

  private async uploadFiles(
    fileList: {
      questionId: number;
      caseAttachmentId: number;
      files: ExtendedUploadFile[];
    }[]
  ): Promise<ApiResponse<QuestionFile[]>[]> {
    try {
      this.loaderService.show();
      const uploadRequests = fileList.map(item =>
        this.questionnairesService.uploadQuestionnaireDocuments(
          item.caseAttachmentId,
          item.questionId,
          item.files
        )
      );
      const responses = await firstValueFrom(
        forkJoin(uploadRequests).pipe(takeUntil(this.destroy$))
      );
      return responses;
    } catch (err) {
      this.commonService.notify('error', 'File upload failed.');
      this.loaderService.hide();
      console.error(err);
      throw err;
    }
  }

  private async prepareQuestionnaireAttachment(): Promise<void> {
    const factory = this.viewContainerRef.createComponent(
      PrintQuestionnairesFormComponent,
      { injector: this.injector }
    );
    factory.location.nativeElement.style.display = 'none';

    try {
      const logoUrl =
        this.organizationDetail?.logoUrl || '/assets/images/logo.png';
      const base64Image = await firstValueFrom(
        from(this.commonService.getBase64ImageFromUrl(logoUrl)).pipe(
          takeUntil(this.destroy$)
        )
      );

      factory.instance.questionnaireData = {
        questionnairesData: this.questionAnswerResponses.responses,
        logo: base64Image,
        attachmentName: this.questionAnswerResponses.attachmentName ?? '',
        caseDetail: this.caseParams?.case,
        orgDetail: this.organizationDetail,
        uploadedFiles: this.uploadedFiles ?? [],
      };
      await this.waitForComponent(factory);
      factory.instance.printPDF();

      await new Promise<void>((resolve, reject) => {
        factory.instance.questionnariePdf
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (file: File) => {
              this.updateAttachment(file);
              factory.destroy();
              resolve();
            },
            error: err => {
              factory.destroy();
              reject(err);
            },
          });
      });
    } catch (err) {
      console.error('Attachment preparation failed', err);
      this.commonService.notify('error', 'Failed to prepare attachment.');
      this.loaderService.hide();
    }
  }

  private async updateAttachment(file: File): Promise<void> {
    const payload = {
      caseId: this.caseParams?.case?.id || 0,
      folderId: 0,
      planTypeId: this.caseParams?.planTypeId || 0,
      name: this.attachmentName ?? '',
      description: 'Questionnaire Document',
      mediaTypeId: '1',
      caseAttachmentId: this.questionAnswerResponses.caseAttachmentId || 0,
      file,
      isQuestionnairePDF: true,
    };

    // Build audit trail for attachment
    const _modifiedFields = this.buildAttachmentModifiedFields();
    if (_modifiedFields.length > 0) {
      (payload as unknown as Record<string, unknown>)['_modifiedFields'] =
        _modifiedFields;
    }

    const response = await this.safeApiCall(() =>
      this.studentAttachmentService.uploadAttachment(payload)
    );

    if (response) {
      this.commonService.notify(
        'success',
        'Questionnaries Document Generated Successfully'
      );
      this.closeDrawer();
    } else {
      console.error('Upload failed or returned no data');
    }
  }

  private waitForComponent(
    factory: ComponentRef<PrintQuestionnairesFormComponent>
  ) {
    return new Promise<void>(resolve => {
      const interval = setInterval(() => {
        if (factory.instance.initialized) {
          clearInterval(interval);
          resolve();
        }
      }, 10);
    });
  }

  getBeforeUploadFn(questionId: number) {
    return (file: NzUploadFile): boolean /* NOSONAR */ => {
      if (!file) {
        this.commonService.notify('error', 'No file selected for upload.');
        return false;
      }
      const control = this.form.controls['que_' + questionId] as FormControl<
        ExtendedUploadFile[]
      >;

      const MAX_FILE_SIZE = 5 * 1024 * 1024;
      if (file.size && file.size > MAX_FILE_SIZE) {
        this.commonService.notify('error', `File "${file.name}" exceeds 5MB`);
        return false;
      }

      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      const isValidType = this.allowedFileExtensions.includes(`.${extension}`);
      if (!isValidType) {
        this.commonService.notify(
          'error',
          `File "${file.name}" has an invalid type. Supported formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, MP3, WAV, M4A, MP4, M4V, TIF, TIFF`
        );
        return false;
      }

      control.setErrors(null);
      const existingFiles = control.value || [];

      const newFile: ExtendedUploadFile = {
        ...file,
        uid: file.uid || String(Date.now()),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        name: file.name || (file as any).originFileObj?.name || 'Unnamed file',
        status: 'done',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        originFileObj: file as any,
        fileId: undefined,
        url: '',
      };

      if (file.type?.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => {
          newFile.url = reader.result as string;
          control.setValue([...existingFiles, newFile]);
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        reader.readAsDataURL(file as any);
      } else {
        control.setValue([...existingFiles, newFile]);
      }

      return false;
    };
  }

  handlePreview = (file: NzUploadFile): void => {
    if (!file) return;
    const extFile = file as ExtendedUploadFile;
    let fileUrl: string | undefined;

    if (extFile.url) {
      fileUrl = extFile.url;
    } else if (extFile.thumbUrl) {
      fileUrl = extFile.thumbUrl;
    } else if (extFile.originFileObj) {
      fileUrl = URL.createObjectURL(extFile.originFileObj);
    }
    if (!fileUrl) {
      return;
    }
    window.open(fileUrl, '_blank');

    if (extFile.originFileObj && !extFile.url) {
      setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
    }
  };

  getRemoveFileFn(questionId: number) {
    return (file: NzUploadFile): boolean => {
      const control = this.form.controls['que_' + questionId] as FormControl<
        NzUploadFile[]
      >;
      control.setValue(control.value.filter(f => f.uid !== file.uid));
      return true;
    };
  }

  async getFilesFromUrl(fileList: FileData[]): Promise<ExtendedUploadFile[]> {
    const files = await Promise.all(
      fileList.map(async file => {
        const response = await fetch(file.fileUrl);
        const blob = await response.blob();
        const type = blob.type;
        const newFile = new File([blob], file.displayName, { type });

        return {
          uid: String(file.id),
          name: file.displayName,
          status: 'done',
          url: file.fileUrl,
          originFileObj: newFile,
          fileId: file.id,
        } as ExtendedUploadFile;
      })
    );

    return files;
  }

  getLinearOptions(linearScale?: LinearScale) {
    if (linearScale?.minScaleValue && linearScale.maxScaleValue) {
      return Array.from(
        { length: linearScale.maxScaleValue - linearScale.minScaleValue + 1 },
        (_, i) => linearScale.minScaleValue + i
      );
    } else {
      return [];
    }
  }

  onCheckboxChange(ctrl: string, checked: boolean, value: number): void {
    this.getControl(ctrl)?.markAsTouched();
    const selectedOptions = this.form.get(ctrl)?.value || [];
    if (checked) {
      selectedOptions.push(value);
    } else {
      const index = selectedOptions.indexOf(value);
      if (index > -1) {
        selectedOptions.splice(index, 1);
      }
    }
    this.form.get(ctrl)?.setValue(selectedOptions);
  }

  onDateChange(date: Date, questionId: number): void {
    const control = this.form.get('que_' + questionId) as FormControl;
    if (!(date instanceof Date)) return;
    const formatted = format(date, 'yyyy-MM-dd');
    if (control.value !== formatted) {
      control.setValue(formatted, { emitEvent: false });
    }
  }

  getControl(controlName: string) {
    return this.form.get(controlName);
  }

  private async safeApiCall<T>(fn: () => Observable<T>): Promise<T | null> {
    try {
      this.loaderService.show();
      return await firstValueFrom(fn().pipe(takeUntil(this.destroy$)));
    } catch (err) {
      this.handleError(err as ErrorResponse);
      this.loaderService.hide();
      return null;
    }
  }

  private handleError({ errors, message }: ErrorResponse): void {
    const messages = errors?.map(e => e.description || e.detailedMessage) || [];
    this.commonService.notify('error', messages, message);
  }

  stringToArray(value: string): number[] {
    return value
      ? value
          .split(',')
          .map(v => Number(v.trim()))
          .filter(Boolean)
      : [];
  }

  convertToDate(timeString: string): Date {
    const [hours, minutes] = timeString.split(':').map(Number);
    const currentDate = new Date();
    currentDate.setHours(hours, minutes, 0, 0);

    return currentDate;
  }

  closeDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.CASE_ATTACHMENT_QUESTIONNAIRE);
  }
}
